import type React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import LoadingState from "../common/LoadingState";
import { CREATE_USER_ROLES, type User, type UserRole } from "../../types/user";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface EditUserRoleModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onSubmit: (userId: string, role: string) => Promise<void>;
  error: string | null;
}

const USER_ROLES = [...CREATE_USER_ROLES];

const EditUserRoleModal: React.FC<EditUserRoleModalProps> = ({
  open,
  onClose,
  user,
  onSubmit,
  error,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | "">("");
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setSelectedRole(user.role || "");
    }
  }, [open, user.role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || selectedRole === user.role) return;

    setLoading(true);
    try {
      await onSubmit(user._id, selectedRole);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <LoadingState message="Updating role..." />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TestId.USERS.EDIT_ROLE_FORM}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("Change Role" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("Change role for {{username}} ({{name}})" as TranslationKeys, {
              username: user._id,
              name: user.name,
            })}
          </Typography>

          <FormControl fullWidth>
            <Autocomplete
              value={USER_ROLES.find((role) => role === selectedRole) || null}
              onChange={(_, newValue) => {
                setSelectedRole(newValue || "");
              }}
              options={USER_ROLES}
              getOptionLabel={(option) => t(option as TranslationKeys)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Role" as TranslationKeys)}
                  required
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.USERS.EDIT_ROLE_SELECT,
                  }}
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !selectedRole || selectedRole === user.role}
            data-testid={TestId.USERS.EDIT_ROLE_SUBMIT}
          >
            {loading ? <CircularProgress size={24} /> : t("Save" as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserRoleModal;
